import { ReactNode } from 'react';
import { InfoButton, Inline, Legend, StatusDot, t } from '@kanbios/ui';
import type { DataTableColumn, DotTone, LegendItem } from '@kanbios/ui';

export type DotVariant = 'solid' | 'ring' | 'halo';

/** What a health dot shows: its colour, its shape, its name (the tooltip) and an optional cause. */
export interface DotSpec {
  tone: DotTone;
  variant?: DotVariant;
  label: string;
  description?: string;
}

/**
 * DSM colour-blind rule (StatusDot): pos · warn · info are safe together,
 * neutral only as a ring, pos vs neg is not: the negative state gets a halo.
 */
export function dotVariant(tone: DotTone): DotVariant {
  if (tone === 'neg') return 'halo';
  if (tone === 'neutral') return 'ring';
  return 'solid';
}

/** A health dot in a table cell: colour only, its name and cause one hover or one Tab away. */
export function HealthDot({ spec }: { spec: DotSpec }) {
  return <StatusDot tone={spec.tone} variant={spec.variant ?? dotVariant(spec.tone)} label={spec.label} description={spec.description || undefined} />;
}

/** The legend of a code: the same markers as the cells. */
export function HealthLegend({ items, label }: { items: DotSpec[]; label?: string }) {
  const legendItems: LegendItem[] = items.map(i => ({ key: i.label, tone: i.tone, variant: i.variant ?? dotVariant(i.tone), label: i.label, description: i.description }));
  return <Legend stacked label={label} items={legendItems} />;
}

/**
 * D53: every table with a health status shows it as a colour only, in its
 * first column, narrow; the header is the « i » that opens the legend
 * (DSM StatusDot + InfoButton + Legend). The word stays in `accessor` so the
 * table search, sort and export still carry it.
 */
export function healthColumn<T>({ legend, legendLabel, legendTitle, spec, key = 'state', title, abbreviation }: {
  /** the Legend shown by the header's InfoButton */
  legend: ReactNode;
  /** accessible name of the InfoButton (« Légende du code couleur ») */
  legendLabel: string;
  legendTitle: string;
  /** the dot of a row, null when the row has no state */
  spec: (row: T) => DotSpec | null;
  key?: string;
  /** D71: a table with two health columns names each one beside its « i » */
  title?: string;
  /** D74: the title as one letter (« S », « A »), the full word in its `abbr` */
  abbreviation?: string;
}): DataTableColumn<T> & { pinned: 'left' } {
  const info = <InfoButton label={legendLabel} title={legendTitle} size="sm">{legend}</InfoButton>;
  const width = abbreviation ? 64 : title ? 120 : 56;
  return {
    key,
    // The header holds a button: no sort (no button inside a button), no resize, never hidden.
    width, minWidth: width, sortable: false, resizable: false, hideable: false, pinned: 'left',
    label: title ? <Inline gap={4} align="center" wrap={false}>{abbreviation ? <abbr title={title}>{abbreviation}</abbr> : <span>{title}</span>}{info}</Inline> : info,
    accessor: (row: T) => spec(row)?.label ?? '',
    render: (row: T) => {
      const s = spec(row);
      return s ? <HealthDot spec={s} /> : null;
    },
  };
}

/** The states of a supervision check (Checkmk), worst first. */
export const CHECK_ORDER = ['crit', 'warn', 'unknown', 'pending', 'ok'] as const;
export type CheckState = (typeof CHECK_ORDER)[number];
/** Critique gets a halo (red beside the green of OK); Inconnu a ring; En attente a plain grey, apart from Inconnu. */
const CHECK_DOTS: Record<CheckState, { tone: DotTone; variant: DotVariant }> = {
  crit: { tone: 'neg', variant: 'halo' },
  warn: { tone: 'warn', variant: 'solid' },
  unknown: { tone: 'neutral', variant: 'ring' },
  pending: { tone: 'neutral', variant: 'solid' },
  ok: { tone: 'pos', variant: 'solid' },
};

export function checkSpec(state: CheckState, description?: string | null): DotSpec {
  return { ...CHECK_DOTS[state], label: t(`checks.${state}`), description: description ?? undefined };
}

export function CheckLegend() {
  return <HealthLegend items={CHECK_ORDER.map(state => ({ ...checkSpec(state), description: t(`checks.rule.${state}`) }))} />;
}

/** D53: the state column of a table of checks; `get` gives the row's check (null: no check yet). */
export function checkColumn<T>(get: (row: T) => { state: CheckState } | null) {
  return healthColumn<T>({
    legendLabel: t('server.health.legend'),
    legendTitle: t('server.health.legendTitle'),
    legend: <CheckLegend />,
    spec: row => {
      const c = get(row);
      return c ? checkSpec(c.state) : null;
    },
  });
}
